import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowUpRight, CalendarCheck, Check, ChevronsUpDown, CircleUserRound, Contact, Crosshair, FolderOpen, House, LayoutGrid, LifeBuoy, LineChart,
  LogOut, Menu, PanelLeftClose, PanelLeftOpen, PencilLine, Plus, Search, SlidersHorizontal, Table2, Upload, UserPlus, UsersRound, Workflow, X,
} from 'lucide-react';
import sidebarMark from 'src/assets/branding/auto-appy-mark-256x256.png';
import { AuthContext } from 'src/context/AuthContext';
import { autoAppyUrl } from 'src/config/autoAppyUrls';
import { autoAppyAppInitials, autoAppyAppLaunchUrl, autoAppySwitcherApps } from 'src/config/autoAppyApps';
import { useWorkspace } from 'src/features/MiniCRM/Workspace/context/WorkspaceContext';
import { FOOTER_ITEMS, accessFor, activeItemId, visibleGroups } from './crmNav';
import { Monogram } from './StageUi';
import CommandPalette, { OPEN_PALETTE_EVENT, QUICK_ADD_EVENT } from 'src/features/Stage/shared/CommandPalette';
import QuickAddLead from 'src/features/Stage/shared/QuickAddLead';
import { countDueFollowUps } from 'src/features/Stage/shared/crmWork';
import './stage.css';
import './crm-stage.css';

// AUTO-CRM-32: the CRM sidebar, built like the Auto Appy sidebar
// (auto-appy src/features/Workspace/WorkspaceSidebar.jsx) so products match.

const ICONS = {
  home: House, leads: Contact, followups: CalendarCheck, targets: Crosshair, insights: LineChart, documents: FolderOpen, team: UsersRound,
  pipelines: Workflow, leadtable: Table2, settings: SlidersHorizontal,
};
const PRODUCT_TONES = { main: '#147DB5', whatsapp: '#1F9D57', crm: '#183F5A' };
const RAIL_KEY = 'autoappy.crm.sidebarRail';

const readRail = () => {
  try { return window.localStorage.getItem(RAIL_KEY) === '1'; } catch { return false; }
};

function usePopover(width = 300) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const anchorRef = useRef(null);
  const panelRef = useRef(null);

  const toggle = useCallback((placement = 'side') => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (rect) {
      if (placement === 'below') {
        setPosition({ left: Math.max(12, Math.min(rect.left, window.innerWidth - width - 12)), top: rect.bottom + 8 });
      } else {
        const left = Math.min(rect.right + 10, window.innerWidth - width - 12);
        setPosition({ left: Math.max(12, left), bottom: Math.max(12, window.innerHeight - rect.bottom) });
      }
    }
    setOpen((value) => !value);
  }, [width]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (!panelRef.current?.contains(event.target) && !anchorRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => { if (event.key === 'Escape') { setOpen(false); anchorRef.current?.focus(); } };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onPointer); document.removeEventListener('keydown', onKey); };
  }, [open]);

  return { open, setOpen, toggle, position, anchorRef, panelRef };
}

function NavLink({ item, active, rail, onNavigate, badge }) {
  const Icon = ICONS[item.icon] || House;
  const label = badge ? `${item.label}, ${badge} due` : item.label;
  return (
    <Link
      to={item.to}
      className={`ws-sb-item${active ? ' is-active' : ''}`}
      aria-current={active ? 'page' : undefined}
      data-tip={rail ? item.label : undefined}
      data-tip-meta={rail && badge ? `${badge} due` : undefined}
      aria-label={rail || badge ? label : undefined}
      onClick={onNavigate}
    >
      <Icon size={18} strokeWidth={1.8} aria-hidden="true" />
      <span className="ws-sb-text">{item.label}</span>
      {badge ? <span className="crm-sb-badge ws-num" aria-hidden="true">{badge > 99 ? '99+' : badge}</span> : null}
    </Link>
  );
}

function LogoutDialog({ onCancel, onConfirm, busy }) {
  const cancelRef = useRef(null);
  useEffect(() => { cancelRef.current?.focus(); }, []);
  return (
    <div className="ws-dialog-backdrop ws-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onCancel(); }}>
      <div className="ws-dialog" role="alertdialog" aria-modal="true" aria-labelledby="crm-logout-title" aria-describedby="crm-logout-copy"
        onKeyDown={(event) => { if (event.key === 'Escape') onCancel(); }}>
        <span className="ws-icon-tile ws-icon-tile--red" aria-hidden="true"><LogOut size={18} /></span>
        <h2 id="crm-logout-title" className="ws-h2" style={{ marginTop: 14 }}>Log out of Auto Appy?</h2>
        <p id="crm-logout-copy" className="ws-muted" style={{ marginTop: 6 }}>You’ll be signed out of the CRM and every Auto Appy product in this browser.</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 22 }}>
          <button ref={cancelRef} type="button" className="ws-btn ws-btn--quiet" onClick={onCancel} disabled={busy}>Stay signed in</button>
          <button type="button" className="ws-btn ws-btn--danger" onClick={onConfirm} disabled={busy}><LogOut size={15} />{busy ? 'Logging out…' : 'Log out'}</button>
        </div>
      </div>
    </div>
  );
}

export default function StageSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout } = useContext(AuthContext);
  const { workspaces, currentWorkspace, setCurrentWorkspaceId } = useWorkspace();
  const [rail, setRail] = useState(readRail);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const products = usePopover(300);
  const account = usePopover(260);
  const org = usePopover(300);
  const create = usePopover(260);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [quickAdd, setQuickAdd] = useState(false);
  const [dueCount, setDueCount] = useState(0);
  const dueCheckedAt = useRef(0);

  const access = accessFor(currentUser);
  const groups = visibleGroups(access);
  const activeId = activeItemId(location.pathname);
  const apps = autoAppySwitcherApps(currentUser?.apps);
  const name = currentUser?.name || 'Your account';
  const workspaceName = currentWorkspace?.name || currentUser?.current_workspace?.name || 'CRM workspace';
  const centralId = currentUser?.current_workspace?.id;

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // ⌘K / Ctrl+K opens search anywhere; other screens can ask for it or for quick add.
  useEffect(() => {
    const onKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') { event.preventDefault(); setPaletteOpen((open) => !open); }
    };
    const openPalette = () => setPaletteOpen(true);
    const openQuickAdd = () => setQuickAdd(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_PALETTE_EVENT, openPalette);
    window.addEventListener(QUICK_ADD_EVENT, openQuickAdd);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener(OPEN_PALETTE_EVENT, openPalette); window.removeEventListener(QUICK_ADD_EVENT, openQuickAdd); };
  }, []);

  // Follow-ups due today (mine) as a badge; refreshed at most once a minute, or when work changes.
  const refreshDue = useCallback((force = false) => {
    if (!currentWorkspace?.id && !currentUser?.local_workspace_id) return;
    if (!force && Date.now() - dueCheckedAt.current < 60000) return;
    dueCheckedAt.current = Date.now();
    countDueFollowUps({ mine: true }).then(setDueCount).catch(() => {});
  }, [currentWorkspace?.id, currentUser?.local_workspace_id]);
  useEffect(() => { refreshDue(); }, [refreshDue, location.pathname]);
  useEffect(() => {
    const onChange = () => refreshDue(true);
    window.addEventListener('crm:followups-changed', onChange);
    return () => window.removeEventListener('crm:followups-changed', onChange);
  }, [refreshDue]);
  useEffect(() => {
    try { window.localStorage.setItem(RAIL_KEY, rail ? '1' : '0'); } catch { /* private mode */ }
  }, [rail]);

  const closeMobile = () => setMobileOpen(false);
  const showRail = rail && !mobileOpen;

  const confirmLogout = async () => {
    setLoggingOut(true);
    try { await logout(); } catch (error) { console.error('Logout failed:', error); } finally { setLoggingOut(false); setLogoutOpen(false); navigate('/'); }
  };

  // Collapsed rail: labels show in a tooltip beside the control. The sidebar
  // clips its overflow, so the tooltip is fixed-position outside it.
  const [tip, setTip] = useState(null);
  const showTip = (event) => {
    const target = event.target.closest?.('[data-tip]');
    if (!showRail || !target) { setTip(null); return; }
    const rect = target.getBoundingClientRect();
    setTip({ label: target.dataset.tip, meta: target.dataset.tipMeta, top: rect.top + rect.height / 2, left: rect.right + 12 });
  };
  const tipHandlers = { onMouseOver: showTip, onFocus: showTip, onMouseLeave: () => setTip(null), onBlur: () => setTip(null), onClick: () => setTip(null) };
  useEffect(() => { if (!showRail) setTip(null); }, [showRail]);

  return (
    <>
      <button type="button" className="ws-hamburger" onClick={() => setMobileOpen((open) => !open)} aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'} aria-expanded={mobileOpen}>
        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
      </button>
      {mobileOpen && <div className="ws-scrim" onClick={closeMobile} aria-hidden="true" />}
      <div className={`ws-sidebar-shell${mobileOpen ? ' is-open' : ''}`}>
        <nav className={`ws-sidebar${showRail ? ' is-rail' : ''}`} aria-label="Main navigation" {...tipHandlers}>
          <div className="ws-sb-top">
            <img src={sidebarMark} alt="" />
            <span className="ws-sb-brand">Auto <span>Appy</span></span>
            <span className="crm-sb-product" aria-label="CRM">CRM</span>
            <button type="button" className="ws-sb-iconbtn" onClick={() => setRail((value) => !value)} aria-label={showRail ? 'Expand sidebar' : 'Collapse sidebar'} data-tip={showRail ? 'Expand sidebar' : undefined} title={showRail ? undefined : 'Collapse sidebar'}>
              {showRail ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}
            </button>
          </div>

          <button type="button" ref={org.anchorRef} className="ws-sb-org" onClick={() => org.toggle('below')} aria-haspopup="dialog" aria-expanded={org.open}
            data-tip={showRail ? workspaceName : undefined} data-tip-meta={showRail ? 'Switch' : undefined} aria-label={`Workspace: ${workspaceName}. Switch workspace`}>
            <span className="ws-sb-org-mark" aria-hidden="true">{autoAppyAppInitials(workspaceName).slice(0, 1)}</span>
            <span className="ws-sb-org-text ws-grow" style={{ minWidth: 0 }}>
              <span className="ws-sb-org-name" style={{ display: 'block' }}>{workspaceName}</span>
              <span className="ws-sb-org-sub">CRM workspace</span>
            </span>
            <ChevronsUpDown className="ws-sb-chevron" size={14} color="#5B7180" aria-hidden="true" />
          </button>

          <div className="crm-sb-quick">
            <button type="button" ref={create.anchorRef} className="crm-sb-new" onClick={() => create.toggle('below')} aria-haspopup="menu" aria-expanded={create.open} data-tip={showRail ? 'New' : undefined} aria-label="Create new">
              <Plus size={16} strokeWidth={2.2} aria-hidden="true" /><span className="ws-sb-text">New</span>
            </button>
            <button type="button" className="crm-sb-search" onClick={() => { closeMobile(); setPaletteOpen(true); }} data-tip={showRail ? 'Search' : undefined} data-tip-meta={showRail ? '⌘K' : undefined} aria-label="Search leads and pages">
              <Search size={15} aria-hidden="true" /><span className="ws-sb-text">Search</span><kbd className="ws-sb-text">⌘K</kbd>
            </button>
          </div>

          <div className="ws-sb-scroll">
            {groups.map((group) => (
              <div className="ws-sb-group" key={group.id} role="group" aria-label={group.label || 'CRM'}>
                {group.label && <div className="ws-sb-label">{group.label}</div>}
                {group.items.map((item) => (
                  <NavLink key={item.id} item={item} active={activeId === item.id} rail={showRail} onNavigate={closeMobile} badge={item.badge === 'due' ? dueCount : 0} />
                ))}
              </div>
            ))}
          </div>

          <div className="ws-sb-footer">
            {FOOTER_ITEMS.map((item) => (
              <NavLink key={item.id} item={item} active={activeId === item.id} rail={showRail} onNavigate={closeMobile} />
            ))}
            <a className="ws-sb-item" href={autoAppyUrl('/help')} data-tip={showRail ? 'Help' : undefined} aria-label={showRail ? 'Help' : undefined}>
              <LifeBuoy size={18} strokeWidth={1.8} aria-hidden="true" />
              <span className="ws-sb-text">Help</span>
            </a>
            <button type="button" ref={products.anchorRef} className="ws-sb-item" onClick={() => products.toggle()} aria-haspopup="dialog" aria-expanded={products.open} data-tip={showRail ? 'Products' : undefined} aria-label={showRail ? 'Products' : undefined}>
              <span className="ws-sb-dots" aria-hidden="true"><i style={{ background: '#147DB5' }} /><i style={{ background: '#7CC4E8' }} /><i style={{ background: '#1F9D57' }} /><i style={{ background: '#183F5A' }} /></span>
              <span className="ws-sb-text">Products</span>
              <ArrowUpRight className="ws-sb-meta" size={14} aria-hidden="true" />
            </button>
            <button type="button" ref={account.anchorRef} className="ws-sb-me" onClick={() => account.toggle()} aria-haspopup="menu" aria-expanded={account.open} aria-label={`Account: ${name}`} data-tip={showRail ? name : undefined}>
              <Monogram name={name} size={32} />
              <span className="ws-sb-org-text ws-grow" style={{ minWidth: 0 }}>
                <span className="ws-sb-org-name" style={{ display: 'block' }}>{name}</span>
                <span className="ws-sb-org-sub" style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentUser?.email || 'Profile and sign out'}</span>
              </span>
              <ChevronsUpDown className="ws-sb-chevron" size={14} color="#5B7180" aria-hidden="true" />
            </button>
          </div>
        </nav>
      </div>
      {tip && createPortal(<div className="crm-rail-tip" role="tooltip" style={{ top: tip.top, left: tip.left }}>{tip.label}{tip.meta && <small>{tip.meta}</small>}</div>, document.body)}

      {create.open && create.position && (
        <div ref={create.panelRef} className="ws-popover ws-overlay" role="menu" aria-label="Create new" style={{ left: create.position.left, top: create.position.top, width: 260 }}>
          <button type="button" role="menuitem" className="ws-menu-item" onClick={() => { create.setOpen(false); closeMobile(); setQuickAdd(true); }}><UserPlus size={16} />Lead</button>
          <button type="button" role="menuitem" className="ws-menu-item" onClick={() => { create.setOpen(false); closeMobile(); navigate('/crm', { state: { openImport: true } }); }}><Upload size={16} />Import leads</button>
          <button type="button" role="menuitem" className="ws-menu-item" onClick={() => { create.setOpen(false); closeMobile(); navigate('/follow-ups'); }}><CalendarCheck size={16} />Plan follow-ups</button>
          {access.canManagePipelines && <button type="button" role="menuitem" className="ws-menu-item" onClick={() => { create.setOpen(false); closeMobile(); navigate('/admin/pipelines?create=1'); }}><Workflow size={16} />Pipeline from a template</button>}
        </div>
      )}

      {paletteOpen && <CommandPalette onClose={() => setPaletteOpen(false)} />}
      {quickAdd && <QuickAddLead onClose={() => setQuickAdd(false)} />}

      {org.open && org.position && (
        <div ref={org.panelRef} className="ws-popover ws-overlay" role="dialog" aria-label="Switch workspace" style={{ left: org.position.left, top: org.position.top }}>
          <div className="ws-popover-title">Workspaces</div>
          {workspaces.length === 0 && <p className="ws-muted" style={{ padding: '4px 10px 10px' }}>No CRM workspaces yet.</p>}
          {workspaces.map((workspace) => {
            const current = workspace.id === currentWorkspace?.id;
            return (
              <button key={workspace.id} type="button" className="ws-menu-item" aria-current={current ? 'true' : undefined}
                onClick={async () => { org.setOpen(false); closeMobile(); if (!current) { await setCurrentWorkspaceId(workspace.id); navigate('/workspace'); } }}>
                <span className="ws-sb-org-mark" aria-hidden="true">{autoAppyAppInitials(workspace.name).slice(0, 1)}</span>
                <span className="ws-grow" style={{ minWidth: 0 }}>
                  <span className="ws-sb-org-name" style={{ display: 'block' }}>{workspace.name}</span>
                  <span className="ws-sb-org-sub" style={{ textTransform: 'capitalize' }}>{workspace.current_user_role || 'Member'}</span>
                </span>
                {current && <Check size={15} color="#147DB5" aria-hidden="true" />}
              </button>
            );
          })}
          <div className="ws-menu-sep" />
          <a className="ws-menu-item" href={autoAppyUrl(`/organisation${centralId ? `?organisation=${encodeURIComponent(centralId)}` : ''}`)} onClick={() => org.setOpen(false)}>
            <UsersRound size={16} aria-hidden="true" />Manage organisation<ArrowUpRight size={14} aria-hidden="true" style={{ marginLeft: 'auto' }} />
          </a>
          <a className="ws-menu-item" href={autoAppyUrl('/organisation')} onClick={() => org.setOpen(false)}>
            <Plus size={16} aria-hidden="true" />New organisation<ArrowUpRight size={14} aria-hidden="true" style={{ marginLeft: 'auto' }} />
          </a>
        </div>
      )}

      {products.open && products.position && (
        <div ref={products.panelRef} className="ws-popover ws-overlay" role="dialog" aria-label="Switch product" style={{ left: products.position.left, bottom: products.position.bottom }}>
          <div className="ws-popover-title">Switch product</div>
          {apps.length === 0 && <p className="ws-muted" style={{ padding: '4px 10px 10px' }}>No other products are enabled for this account.</p>}
          {apps.map((app) => (
            <a key={app.key} className="ws-menu-item" href={autoAppyAppLaunchUrl(app, app.key === 'crm' ? currentWorkspace?.id : centralId)} onClick={() => products.setOpen(false)} aria-current={app.key === 'crm' ? 'page' : undefined}>
              <span className="ws-product-icon" style={{ background: PRODUCT_TONES[app.key] || '#3A5264' }}>{autoAppyAppInitials(app.name)}</span>
              <span className="ws-grow">{app.key === 'main' ? 'Auto Appy' : app.name}</span>
              {app.key === 'crm' ? <span className="ws-chip">Current</span> : <ArrowUpRight size={15} aria-hidden="true" />}
            </a>
          ))}
          <a className="ws-menu-item" href={autoAppyUrl('/autoappy/apps')} onClick={() => products.setOpen(false)} style={{ color: '#0F6392', fontWeight: 500 }}>
            <LayoutGrid size={16} aria-hidden="true" />View the complete app library
          </a>
        </div>
      )}

      {account.open && account.position && (
        <div ref={account.panelRef} className="ws-popover ws-overlay" role="menu" aria-label="Account" style={{ left: account.position.left, bottom: account.position.bottom, width: 260 }}>
          <a role="menuitem" className="ws-menu-item" href={autoAppyUrl('/profile')} onClick={() => account.setOpen(false)}><CircleUserRound size={16} />Profile</a>
          <a role="menuitem" className="ws-menu-item" href={autoAppyUrl('/edit-profile')} onClick={() => account.setOpen(false)}><PencilLine size={16} />Edit profile</a>
          <button role="menuitem" type="button" className="ws-menu-item" onClick={() => { account.setOpen(false); setLogoutOpen(true); }} style={{ color: '#AD3B3B' }}><LogOut size={16} color="#AD3B3B" />Log out</button>
        </div>
      )}
      {logoutOpen && <LogoutDialog busy={loggingOut} onCancel={() => setLogoutOpen(false)} onConfirm={confirmLogout} />}
    </>
  );
}
